import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "@/common/ui/Button";
import { SideBarContentHeader } from "@/common/ui/SideBar";
import { ModalConfig, withModal } from "@/provider/withModalProvider";
import { ButtonWithActions } from "@/common/ActionsButton";
import { Center, Row, VerticalCenter } from "@/common/css";
import Icon, { Image } from "@/common/ui/Image";
import { H2, LightText2 } from "@/common/ui/Text";
import { GuardedLink } from "@/common/custom/GuardedLink";
import AddAttachments from "@/components/employee/modal/AddAttachments";
import OverviewComponent from "@/dmr/components/QuickNote";
import AddAndEditNotePopup from "@/dmr/components/AddAndEditNotePopup";
import { SocialLinks } from "@/common/custom/SocialLinks";
import Overview from "./Overview";
import EmployeeInfo from "./EmployeeInfo";
import { Attachments } from "./Attachments";
import ExitDetails from "./ExitDetails";
import { useAppDispatch } from "@/store/store";
import { employeeSelector, setEmployeeInfo, setNoteEditor } from "@/store/features/employeeSlice";
import { useSelector } from "react-redux";
import { useEmployeeInfo } from "@/services/employee.services";
import CopyText from "@/common/custom/CopyText";
import { ProfileIcon } from "@/common/input/SelectBoxFromApi";
import EditEmployee from "@/components/employee/modal/EditEmployee";
import ExitEmployee from "@/components/employee/modal/ExitTermination";
import { Activities } from "./Activities";
import DeleteNote from "./modal/DeleteNote";
import { getStatusColor } from "@/utils/helper/string";
import { EmployeeDetailsSkeleton } from "@/common/ui/Loader";
import { Menu, ChevronDown } from "lucide-react";
import { PERMISSIONS } from "@/utils/constant/global.constant";
import { loginSelector } from "@/store/features/loginSlice";
import { isHasPermission } from "@/utils/helper/permissions";
import { pageEventSelector } from "@/store/features/dashboardSlice";

const modalConfig: ModalConfig = {
  AddAttachments: {
    component: AddAttachments,
    title: "Upload attachments",
    modalClassName: "!max-w-[400px]",
    type: "side",
  },
  edit: {
    component: EditEmployee,
    title: "Edit employee",
    type: "side",
    isQueryRequired: true,
    showCloseWarning: true,
  },
  "exit-terminate": {
    component: ExitEmployee,
    title: "Exit termination",
    type: "side",
    isQueryRequired: true,
    showCloseWarning: true,
  },
  DeleteNote: {
    component: DeleteNote,
    title: "Delete",
    type: "center",
    isQueryRequired: true,
  },
};

interface EmployeeDetailsPageProps {
  openModal: (modalKey: string, props?: any) => void;
  activeModal: string;
  isForEmploymentDetails?: boolean;
}
interface SidebarContent {
  type: "view";
}

export interface EmployeeSidebar {
  name: string;
  key: string;
  apiKey: string;
  userTypes: string[];
  roles: string[];
  content: SidebarContent;
  render: any;
}

const sidebars: EmployeeSidebar[] = [
  {
    name: "Overview",
    key: "/overview",
    apiKey: "overview",
    userTypes: ["ORG"],
    roles: ["Account Owner"],
    content: { type: "view" },
    render: Overview,
  },
  {
    name: "Employee details",
    key: "/employee-details",
    apiKey: "employee-details",
    userTypes: ["ORG"],
    roles: ["Account Owner"],
    content: { type: "view" },
    render: EmployeeInfo,
  },
  {
    name: "Attachments",
    key: "/attachments",
    apiKey: "attachment",
    userTypes: ["ORG"],
    roles: ["Account Owner"],
    content: { type: "view" },
    render: Attachments,
  },
  {
    name: "Exit details",
    key: "/exit-details",
    apiKey: "exit",
    userTypes: ["ORG"],
    roles: ["Account Owner"],
    content: { type: "view" },
    render: ExitDetails,
  },
  {
    name: "Activities",
    key: "/activities",
    apiKey: "activities",
    userTypes: ["ORG"],
    roles: ["Account Owner"],
    content: { type: "view" },
    render: Activities,
  },
];

const EmployeeDetailsPage: React.FC<EmployeeDetailsPageProps> = ({ openModal, isForEmploymentDetails }) => {
  const { employeeId, tab } = useParams();
  const { currentUser } = useSelector(loginSelector);
  const pageEvent = useSelector(pageEventSelector);
  const { isLoading, data: employeeBasicInfo, refetch } = useEmployeeInfo({ empId: employeeId, view_typ: "basic" });
  const dispatch = useAppDispatch();
  const { view } = useSelector(employeeSelector);
  const navigate = useNavigate();
  const tabInfo = sidebars.find((t) => t.key === `/${tab}`);
  const [isMobileSubMenuOpen, setIsMobileSubMenuOpen] = useState(false);

  const handleMarkAsExEmployee = () => {
    openModal("exit-terminate", { afterCall: refetch });
  };

  const handleEditEmployee = () => {
    openModal("edit", { afterCall: refetch });
  };

  // Filter sidebars based on employee status
  const filteredSidebars = sidebars.filter((item) => {
    if (
      (!employeeBasicInfo?.status?.label && item.key === "/exit-details") ||
      (employeeBasicInfo?.status?.label === "Active" && item.key === "/exit-details")
    ) {
      return false;
    }
    return true;
  });
  const childOptions = [
    employeeBasicInfo?.userTyp?.label !== "Account Owner" && isHasPermission(currentUser?.perms, PERMISSIONS.EMPLOYEE["Edit Employees"])
      ? {
          name: "edit",
          label: "Edit",
          onClick: handleEditEmployee,
        }
      : null,
    ...(employeeBasicInfo?.status?.label === "Active" &&
    employeeBasicInfo?.userTyp?.label !== "Account Owner" &&
    isHasPermission(currentUser?.perms, PERMISSIONS.EMPLOYEE["Export Employees"])
      ? [
          {
            name: "mark-as-ex",
            label: "Mark as ex-employee",
            onClick: handleMarkAsExEmployee,
          },
        ]
      : []),
  ].filter(Boolean);

  useEffect(() => {
    if (tab) {
      if (!tabInfo) {
        navigate("/home/employees", { replace: true });
      }
    }
  }, [tab, tabInfo]);

  useEffect(() => {
    if (employeeBasicInfo) {
      dispatch(setEmployeeInfo(employeeBasicInfo));
    }
    return () => {
      dispatch(setEmployeeInfo(null));
    };
  }, [employeeBasicInfo]);

  useEffect(() => {
    if (pageEvent?.keys.includes("basicInfo") && pageEvent?.type === "refresh") {
      refetch();
    }
  }, [pageEvent]);

  if (isLoading) return <EmployeeDetailsSkeleton tab={tab} />;

  return (
    <div className="flex flex-col flex-1 h-full">
      {/* Mobile Submenu Button */}
      <div className={`md:hidden absolute top-14 right-2 ${isMobileSubMenuOpen ? "z-50" : "z-20"}`}>
        <Button
          variant="outline"
          size="small"
          onClick={() => setIsMobileSubMenuOpen(!isMobileSubMenuOpen)}
          className="rounded-50 text-text-primary border-text-primary !px-3 !py-1 flex items-center gap-1"
        >
          <Menu className="h-4 w-4" />
          <ChevronDown className="h-4 w-4" />
        </Button>

        {/* Mobile Submenu Overlay */}
        {isMobileSubMenuOpen && (
          <>
            <div className="fixed inset-0 z-50  bg-black bg-opacity-50" onClick={() => setIsMobileSubMenuOpen(false)} />
            <div className="fixed top-24 right-2 z-50  p-2 bg-containerBg rounded-lg shadow-lg border border-gray-200 flex items-center gap-2">
              <div className="bg-white rounded-lg shadow-lg min-w-64 max-h-96 overflow-y-auto">
                <div className="p-2">
                  <nav className="space-y-2">
                    {filteredSidebars.map((item) => (
                      <button
                        key={item.key}
                        onClick={() => {
                          navigate(`/home/employees/${employeeId}${item.key}`);
                          setIsMobileSubMenuOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          item.key === `/${tab}` ? "text-text-primary bg-primaryOpacity-10 font-bold" : "text-text-default"
                        } hover:text-text-primary hover:bg-primaryOpacity-10`}
                      >
                        {item.name}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <SideBarContentHeader className="px-1 md:px-4 !border-b-0 ">
        <Center className="!justify-start flex-wrap md:flex-nowrap">
          {employeeBasicInfo?.empName?.profilePic ? (
            <Image src={employeeBasicInfo.empName.profilePic} alt="profile" className="size-12 2xl:size-14 rounded-full" />
          ) : (
            <ProfileIcon className="!size-12 2xl:!size-14 text-lg border-2" text={employeeBasicInfo?.empName?.profileText} />
          )}
          <VerticalCenter className="px-2 !items-start ">
            <div className="flex items-center gap-4 w-full">
              <H2 text={employeeBasicInfo?.empName?.label} />
              {employeeBasicInfo?.social && (
                <Center className="gap-3 hidden md:flex ">
                  <SocialLinks social={employeeBasicInfo.social} />
                </Center>
              )}
              {employeeBasicInfo?.status && (
                <LightText2
                  text={employeeBasicInfo.status?.label}
                  className={`whitespace-nowrap ${getStatusColor(employeeBasicInfo.status?.label)} p-1 px-3 rounded-md font-medium`}
                />
              )}
            </div>
            {employeeBasicInfo?.address && (
              <CopyText
                text={employeeBasicInfo?.address}
                successMessage="Employee address copied!"
                errorMessage="Copy failed"
                className=""
                textClassName="truncate line-clamp-2 max-w-[100px] md:max-w-[300px] xl:max-w-[400px]"
              />
            )}
            {employeeBasicInfo?.social && (
              <Center className="gap-3 md:hidden ">
                <SocialLinks social={employeeBasicInfo.social} />
              </Center>
            )}
          </VerticalCenter>
        </Center>

        <Center className="gap-2 h-full">
          <Button
            variant="outline"
            size="small"
            onClick={() => openModal("AddAttachments")}
            className="rounded-50 text-text-primary border-text-primary !px-3 hidden md:flex"
            leftIcon={<Icon src="/icons/file-upload-icon.svg" alt="icon" />}
          >
            Add attachments
          </Button>
          {childOptions.length > 0 && <ButtonWithActions childOptions={childOptions as any} dropdownClassName="top-9 right-0" />}
        </Center>
      </SideBarContentHeader>
      <Row className="w-full h-[calc(100%-100px)] relative">
        <div className="h-full bg-containerBg w-[216px] shadow-sm rounded-llg hidden md:block mx-3">
          <ul className="p-3 text-text-default">
            {filteredSidebars.map((item) => {
              const isActive = `/${tab}` === item.key;
              return (
                <GuardedLink to={`${isForEmploymentDetails ? "/home/employment-details/" : "/home/employees/"}${employeeId}${item.key}`} key={item.key}>
                  <li
                    className={`px-3 py-2 text-13 rounded-md transition-all duration-100 ${
                      isActive ? "text-text-primary bg-primaryOpacity-10 font-bold " : " text-text-default"
                    } hover:text-text-primary hover:bg-primaryOpacity-10`}
                  >
                    {item.name}
                  </li>
                </GuardedLink>
              );
            })}
          </ul>
        </div>
        <div className="w-full md:w-[calc(100%-216px)] flex h-full mx-0 md:mx-3 rounded-llg border border-stroke-light">
          <div className={`${tabInfo?.key === "/overview" ? "w-full md:w-[70%] " : " w-full"} h-full flex`}>
            <div className="flex w-full flex-col h-full">
              {tabInfo?.render && React.createElement(tabInfo.render, { tab: tabInfo, openModal, empInfo: employeeBasicInfo, isForEmploymentDetails })}
            </div>
          </div>
          {tabInfo?.key === "/overview" && (
            <div className=" hidden md:block md:w-[30%] h-full pt-[50px] overflow-y-auto">
              <OverviewComponent
                setIsVisible={(value: boolean) => dispatch(setNoteEditor(value))}
                openModal={openModal}
                isForEmploymentDetails={isForEmploymentDetails}
              />
            </div>
          )}
        </div>
        <AddAndEditNotePopup
          isVisible={view.isNoteEditorOpen}
          setIsVisible={() => dispatch(setNoteEditor(true))}
          onClose={() => dispatch(setNoteEditor(false))}
          noteData={view.note}
          heading={view.note ? "Edit note" : "Add note"}
          minimizedPopupHeading="Notes"
          primaryButtonDetails={{
            text: view.note ? "Update" : "Done",
          }}
          secondaryButtonDetails={{
            text: "Cancel",
          }}
        />
      </Row>
    </div>
  );
};

export const EmployeeDetailsPageWithModal = withModal(EmployeeDetailsPage, modalConfig);
